import React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import HaveAGoodayLogo from "@/components/ui/HaveAGoodayLogo";
import { NotificationBell } from "@/features/notifications";

export type SidebarHeaderProps = {
  collapsed?: boolean;
  onNavigateHome: () => void;
  logoSize?: number;
  compact?: boolean;
  ultraCompact?: boolean;
};

export function SidebarHeader({
  collapsed = false,
  onNavigateHome,
  logoSize = 36,
  compact: compactProp,
  ultraCompact: ultraCompactProp,
}: SidebarHeaderProps) {
  const theme = useTheme();
  const isCompactViewport = useMediaQuery("(max-width:1400px)");
  const isUltraCompactViewport = useMediaQuery("(max-width:1200px)");
  const isShortViewport = useMediaQuery("(max-height:860px)");
  const compact = compactProp ?? (isCompactViewport || isShortViewport);
  const ultraCompact = ultraCompactProp ?? (isUltraCompactViewport || isShortViewport);
  const showLogoText = !collapsed && !ultraCompact && !compact;
  const resolvedLogoSize = ultraCompact ? 28 : compact ? 32 : logoSize;

  return (
    <Box
      sx={{
        px: collapsed ? 0.5 : ultraCompact ? 0.5 : { xs: 0.75, sm: 1.25 },
        pt: ultraCompact ? 0.5 : { xs: 0.75, sm: 1 },
        pb: ultraCompact ? 0.5 : { xs: 0.65, sm: 0.9 },
        mb: ultraCompact ? 0.75 : compact ? 1.1 : { xs: 1.25, sm: 1.5 },
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: collapsed ? "column" : "row",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: collapsed ? 0.35 : 0.75,
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        onClick={onNavigateHome}
        sx={{
          flex: collapsed ? "0 0 auto" : "1 1 auto",
          minWidth: 0,
          maxWidth: collapsed ? "100%" : "calc(100% - 2.75rem)",
          cursor: "pointer",
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-start",
          width: collapsed ? "100%" : "auto",
          overflow: "hidden",
        }}
      >
        <HaveAGoodayLogo
          size={resolvedLogoSize}
          showText={showLogoText}
          variant="default"
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            justifyContent: collapsed ? "center" : "flex-start",
            overflow: "hidden",
            "& > div:last-of-type span": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-end",
          flex: "0 0 auto",
          flexShrink: 0,
          width: collapsed ? "100%" : "auto",
        }}
      >
        <NotificationBell collapsed={collapsed} />
      </Box>
    </Box>
  );
}
